// AyurvedaProgram.jsx
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0, transition: { duration: 0.25 } },
};

const modalVariants = {
    hidden: {
        opacity: 0,
        scale: 0.85,
        y: 80,
    },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring',
            damping: 28,
            stiffness: 280,
            duration: 0.45,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.9,
        y: 60,
        transition: { duration: 0.25, ease: 'easeIn' },
    },
};

const pulseVariants = {
    pulse: {
        scale: [1, 1.12, 1],
        boxShadow: [
            '0 0 0 0 rgba(16, 185, 129, 0.4)',
            '0 0 0 15px rgba(16, 185, 129, 0.0)',
            '0 0 0 0 rgba(16, 185, 129, 0.0)',
        ],
        transition: {
            duration: 2.2,
            repeat: Infinity,
            repeatType: 'loop',
            ease: 'easeInOut',
        },
    },
};

const AyurvedaProgram = () => {
    const [isOpen, setIsOpen] = React.useState(false);

    // Handle body scroll lock
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        if (isOpen) window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen]);

    return (
        <>
            {/* Star-like Floating Action Button */}
            <motion.button
                variants={pulseVariants}
                animate="pulse"
                whileHover={{ scale: 1.18, transition: { duration: 0.3 } }}
                whileTap={{ scale: 0.92 }}
                onClick={() => setIsOpen(true)}
                className={`
          fixed z-50
          bottom-5 right-5 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8
          w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20
          rounded-full
          bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600
          text-white font-semibold text-xs sm:text-sm leading-tight
          shadow-xl hover:shadow-2xl focus:shadow-2xl
          flex items-center justify-center text-center
          focus:outline-none focus:ring-4 focus:ring-emerald-400/60 focus:ring-offset-2
          transition-all duration-300
          overflow-hidden
          isolate
        `}
                aria-label="Open Ayurveda Program details"
            >
                {/* Optional inner glow / star sparkle layer */}
                <span className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping opacity-70" />
                <span className="relative z-10">
                    Ayurveda
                    <br />
                    Program
                </span>
            </motion.button>

            {/* Modal */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
                        variants={backdropVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={() => setIsOpen(false)}
                    >
                        {/* Backdrop */}
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                        {/* Modal content */}
                        <motion.div
                            variants={modalVariants}
                            className="
                relative w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl
                max-h-[90vh] sm:max-h-[88vh] overflow-y-auto
                bg-white dark:bg-gray-900/95
                rounded-2xl sm:rounded-3xl
                shadow-2xl ring-1 ring-gray-200/60 dark:ring-gray-800/60
                backdrop-blur-sm
              "
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Sticky Header */}
                            <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 px-5 sm:px-7 md:px-8 py-4 sm:py-5 flex items-start justify-between">
                                <div>
                                    <p className="text-emerald-600 dark:text-emerald-400 text-xs sm:text-sm font-semibold tracking-widest uppercase">
                                        AYURVEDA TREATMENT PROGRAMME
                                    </p>
                                    <h2 className="mt-1.5 text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white leading-tight">
                                        Your Journey to Balance, Detox & Renewal
                                    </h2>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.15, rotate: 90 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => setIsOpen(false)}
                                    className="
                    text-gray-500 hover:text-gray-800
                    dark:text-gray-400 dark:hover:text-gray-200
                    text-3xl sm:text-4xl leading-none
                    transition-transform
                  "
                                    aria-label="Close Ayurveda Program modal"
                                >
                                    ×
                                </motion.button>
                            </div>

                            {/* Scrollable Content */}
                            <div className="px-5 sm:px-7 md:px-8 pb-10 pt-6 sm:pt-8 md:pt-10 space-y-8 sm:space-y-10 md:space-y-12 text-gray-700 dark:text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                                <p>
                                    Experience authentic Ayurvedic healing in a peaceful retreat designed to restore your body, calm your mind, and renew your energy.
                                </p>

                                <section>
                                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-emerald-700 dark:text-emerald-400 mb-4 sm:mb-5">
                                        Welcome & Gentle Start
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 list-none">
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Airport pick-up and transfer to the hotel
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Warm welcome and hotel orientation
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Introduction to your weekly  schedule
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Relaxation Massage (Day 1) to release travel fatigue
                                        </li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-emerald-700 dark:text-emerald-400 mb-4 sm:mb-5">
                                        Doctor Consultation & Personal Plan
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 list-none">
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Ayurvedic Doctor Consultation (Day 2 – morning)
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Full assessment of your Dosha / body type and health concerns
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Personalized treatment plan tailored to your needs and goals
                                        </li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-emerald-700 dark:text-emerald-400 mb-4 sm:mb-5">
                                        Daily Ayurveda Therapies
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 list-none">
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            4–5 treatments daily (as recommended by the doctor)
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Therapies selected according to your body constitution
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Daily progress monitoring and treatment adjustments
                                        </li>
                                    </ul>
                                </section>

                                <section>
                                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-emerald-700 dark:text-emerald-400 mb-4 sm:mb-5">
                                        Closing Ritual & Departure
                                    </h3>
                                    <ul className="space-y-3 sm:space-y-4 list-none">
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Beautiful Flower Bath (last day) – traditional renewal ritual
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Final consultation with home care recommendations
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-emerald-600 dark:text-emerald-400 mr-3 sm:mr-4 text-xl sm:text-2xl">•</span>
                                            Airport transfer
                                        </li>
                                    </ul>
                                </section>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AyurvedaProgram;